import React from "react";
import { UserCircle, Users } from "lucide-react";

export default function ChatListUI({
  chats,
  isLoading,
  currentUserId,
  onSelectChat,
  selectedChatId,
}) {
  if (isLoading) return <div className="p-4">Loading chats...</div>;
  if (!chats || chats.length === 0)
    return <div className="p-4 text-gray-500">No chats found</div>;

  const getLastMessagePreview = (chat) => {
    const msg = chat.latestMessage;
    if (!msg) return "No messages yet";

    // ✅ Handle NEW format with files array
    if (msg.files && msg.files.length > 0) {
      const fileCount = msg.files.length;
      
      // Multiple files
      if (fileCount > 1) {
        const types = {
          image: msg.files.filter(f => f.type === "image").length,
          video: msg.files.filter(f => f.type === "video").length,
          audio: msg.files.filter(f => f.type === "audio").length,
          document: msg.files.filter(f => f.type === "document").length,
        };

        if (types.image > 0 && types.image >= types.video) {
          return `📷 ${fileCount} ${fileCount === 1 ? 'Photo' : 'Photos'}`;
        } else if (types.video > 0) {
          return `🎥 ${fileCount} ${fileCount === 1 ? 'Video' : 'Videos'}`;
        } else if (types.audio > 0) {
          return `🎵 ${fileCount} ${fileCount === 1 ? 'Audio' : 'Audios'}`;
        } else if (types.document > 0) {
          return `📄 ${fileCount} ${fileCount === 1 ? 'Document' : 'Documents'}`;
        } else {
          return `📎 ${fileCount} Files`;
        }
      }

      // Single file
      const file = msg.files[0];
      switch (file.type) {
        case "image":
          return "📷 Photo";
        case "video":
          return "🎥 Video";
        case "audio":
          return "🎵 Audio";
        case "document":
          return "📄 Document";
        default:
          return "📎 File";
      }
    }

    // ✅ Handle OLD format with fileType and fileUrl
    if (msg.fileType && msg.fileType !== "none") {
      switch (msg.fileType) {
        case "image":
          return "📷 Photo";
        case "video":
          return "🎥 Video";
        case "audio":
          return "🎵 Audio";
        case "document":
          return "📄 Document";
        default:
          return "📎 File";
      }
    }

    // ✅ Text message
    let content = msg.content || "Message";
    
    // Truncate long messages
    if (content.length > 30) {
      content = content.slice(0, 30) + "...";
    }

    // Add sender name for group chats
    if (chat.isGroupChat && msg.sender) {
      const senderName =
        msg.sender._id === currentUserId
          ? "You"
          : msg.sender.username || "Unknown";
      return `${senderName}: ${content}`;
    }

    return content;
  };

  return (
    <div className="p-4 space-y-3 overflow-y-auto h-full">
      {chats.map((chat) => {
        let title = "";
        let subtitle = "";

        if (chat.isGroupChat) {
          title = chat.chatName || "Unnamed Group";
          subtitle = `Admin: ${
            chat.groupAdmin?.firstName || chat.groupAdmin?.username || "Unknown"
          }`;
        } else {
          const otherUser = chat.users.find((u) => u._id !== currentUserId);
          title =
            `${otherUser?.firstName || ""} ${otherUser?.lastName || ""}`.trim() ||
            otherUser?.username ||
            "Unknown User";
          subtitle = otherUser?.department || otherUser?.email;
        }

        const isActive = selectedChatId === chat._id;

        return (
          <div
            key={chat._id}
            onClick={() => onSelectChat(chat)}
            className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition ${
              isActive ? "bg-blue-50 border-blue-400" : "hover:bg-gray-100"
            }`}
          >
            {/* Avatar / Group Icon */}
            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-gray-200 rounded-full">
              {chat.isGroupChat ? (
                <Users className="w-6 h-6 text-primary" />
              ) : (
                <UserCircle className="w-8 h-8 text-gray-500" />
              )}
            </div>

            {/* Chat Info */}
            <div className="flex-1 min-w-0 overflow-hidden">
              {/* Chat Title */}
              <p className="font-semibold truncate text-sm">{title}</p>

              {/* Last Message Preview */}
              <p className="text-xs text-gray-500 truncate whitespace-nowrap overflow-hidden">
                {getLastMessagePreview(chat)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}